import React from 'react'
import Grid from '@material-ui/core/Grid'
import Skeleton from '@material-ui/lab/Skeleton'
import cx from 'classnames'
import { searchTitles } from 'pages/search/PagedSearchResults'
import { range } from 'libraries/lodash'
import { MENU_ITEMS } from 'constants/menu'
import { menuItemFilter } from 'components/search/Facets'
import LeftDrawerWrapperContainer from 'redux/containers/components/layout/LeftDrawerWrapperContainer'
import selectIsLimitedAccess from 'redux/selectors/selectIsLimitedAccess'
import SearchTypeButtonContainer from 'redux/containers/components/search/SearchTypeButtonContainer'
import { useSelector } from 'react-redux'

const SearchResultsSkeleton = ({ flavour, searchType, pageView = 'left', windowHeight, headerHeight, isDealwatch = false }) => (
  <div className='relative'>
    <div
      className={cx(
        'w-100 overflow-y-auto bg-ghost-grey flex justify-center scrolling-container',
        { 'flex-wrap': !isDealwatch }
      )}
      style={{ maxHeight: `${windowHeight - headerHeight}px` }}
    >
      <LeftDrawerWrapperContainer {...{ pageView }}>
        <div className='bg-ghost-grey'>
          <FacetsContentSkeleton {...{ searchType }} />
        </div>
      </LeftDrawerWrapperContainer>
      <SearchResultsMainSkeleton {...{ flavour, searchType }} />
    </div>
  </div>
)

export const ResultItemSkeleton = () => (
  <li className='mt7 mb3'>
    <QuestionSkeleton />
    <PathBreadCrumbsSkeleton />
    <ExcerptSkeleton />
  </li>
)

const SearchResultsMainSkeleton = ({ flavour, searchType }) => {
  const title = searchTitles(flavour)[searchType] || 'Search results'

  return (
    <div className='grow-1 shrink-1 basis0 ph0 ma0 w-100'>
      <div className='min-vh-100 mb8 layout-width-constraint'>
        <div className='bb b--lite-grey'>
          <section className='bg-ghost-grey'>
            <section className='f4 fw6 pb5 pt6'>
              {title}
            </section>
            <div className='bg-white pl6 pv3'>
              <div className='o-50'>
                <Skeleton classes={{ root: 'w14 bg-cadet-blue ', text: 'f6 lh-title' }} />
              </div>
            </div>
          </section>
          <section>
            <ul className='flex flex-column list-reset list-item-reset pl0'>
              {range(0, 9).map((_, index) => <ResultItemSkeleton key={index} />)}
            </ul>
          </section>
        </div>
      </div>
    </div>
  )
}

const FacetsContentSkeleton = ({ searchType }) => (
  <div className='ph6'>
    <h2 className='mb7 f4 b black lh-title'>
      Search type
    </h2>
    <Grid container className='mb7 w-100'>
      <SearchOptionsSkeleton {...{ searchType }} />
    </Grid>
  </div>
)

const SearchOptionsSkeleton = ({ searchType }) => {
  const isLimitedAccess = useSelector(selectIsLimitedAccess)

  // MENU_ITEMS item.hideFromCore may need to be updated here to check Clarilis role later..
  const searchOptions = MENU_ITEMS.filter(item =>
    menuItemFilter(item) && (!isLimitedAccess || !item.hideFromCore)
  )

  return searchOptions.map(({ title, activeClass, typeName, icon }) => (
    <SearchOptionSkeleton
      key={typeName}
      {...{
        searchType,
        typeName,
        icon,
        title,
        activeClass
      }}
    />
  ))
}

const SearchOptionSkeleton = ({ searchType, typeName, searchPhrase, icon, title, activeClass }) => {
  const isActive = searchType === typeName

  return (
    <Grid item xs={12} className='mb4'>
      <SearchTypeButtonContainer
        isProxy
        searchTerm={searchPhrase}
        {...{ title, activeClass }}
        selected={isActive}
        searchType={typeName}
        iconName={icon}
      />
    </Grid>
  )
}

const QuestionSkeleton = () => (
  <h2 className='ma0 lh-copy f5 fw6'>
    <Skeleton classes={{ root: 'w15 bg-dim-grey', text: 'f5 lh-title' }} />
  </h2>
)

const PathBreadCrumbsSkeleton = () => (
  <ul className='flex list-reset pl0 mt2 mb3'>
    {range(0, 3).map((_, index) => (
      <li key={index} className='f7 di mr6'>
        <div className='o-50'>
          <Skeleton classes={{ root: 'w12 bg-cadet-blue o-50', text: 'f5' }} />
        </div>
      </li>
    ))}
  </ul>
)

const ExcerptSkeleton = () => (
  <div style={{ width: '100%' }}>
    <Skeleton classes={{ root: 'w-100 bg-grey', text: 'f5' }} />
    <Skeleton classes={{ root: 'w-100 bg-grey', text: 'f5' }} />
    <Skeleton classes={{ root: 'w-100 bg-grey', text: 'f5' }} />
  </div>
)

export default SearchResultsSkeleton
